import React from "react";
import axios from "axios";
import Layout from "../common/Layout";

export const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
export const ACCENT = "#095228"; // dark green already used for primary actions elsewhere in this app

// The shared Layout component reads its content from a prop spelled `childern`
// (sic), so regular JSX children are silently dropped. Wrap it here so the
// pages can use normal children.
export const PageShell = ({ children }) => <Layout childern={children} />;

export function getAuthToken() {
  return sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken");
}

const client = (extra = {}) => ({
  headers: { token: getAuthToken(), "Content-Type": "application/json" },
  timeout: 15000,
  ...extra,
});

export const api = {
  get: (path) => axios.get(`${API_BASE_URL}/v1/artist-application/${path}`, client()),
  post: (path, body) => axios.post(`${API_BASE_URL}/v1/artist-application/${path}`, body, client()),
};

// Unauthenticated call for the public artist page: no token header is sent, so
// visitors who are not logged in can use it too.
export const publicApi = {
  get: (path) => axios.get(`${API_BASE_URL}/v1/artist-application/${path}`, { timeout: 15000 }),
};

export const LICENSE_TYPES = [
  { value: "personal", label: "Personal use" },
  { value: "commercial", label: "Commercial use" },
  { value: "sync", label: "Sync (film / video)" },
  { value: "broadcast", label: "Broadcast" },
  { value: "remix", label: "Remix" },
];

export const formatDuration = (seconds) => {
  const total = Number(seconds);
  if (!Number.isFinite(total) || total <= 0) return "";
  return `${Math.floor(total / 60)}:${String(Math.floor(total % 60)).padStart(2, "0")}`;
};

export const formatPrice = (price, currency) => {
  if (price === null || price === undefined) return "Price on request";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD" }).format(price);
  } catch (e) {
    return `${currency || ""} ${price}`.trim();
  }
};

export const errorMessage = (error, fallback = "Something went wrong. Please try again.") =>
  error?.response?.data?.message || fallback;

export const STATUS_LABELS = {
  pending: "Pending Review",
  approved: "Approved",
  rejected: "Not Approved",
};

// Verification status as the artist sees it (ticket: Not Submitted / Pending Review / Verified /
// Rejected / Resubmission Required). `application` is the latest application, or null.
export function verificationStatus(application) {
  if (!application) return { key: "not_submitted", label: "Not Submitted", tone: "gray" };
  if (application.status === "pending") return { key: "pending", label: "Pending Review", tone: "amber" };
  if (application.status === "approved") return { key: "approved", label: "Verified", tone: "green" };
  if (application.resubmission_required) return { key: "resubmission_required", label: "Resubmission Required", tone: "amber" };
  return { key: "rejected", label: "Rejected", tone: "red" };
}

// ID document types. Two-sided documents need a front and a back image.
export const ID_TYPE_OPTIONS = [
  { value: "passport", label: "Passport", twoSided: false },
  { value: "national_id", label: "National ID", twoSided: true },
  { value: "driving_licence", label: "Driving licence", twoSided: true },
  { value: "other_government_id", label: "Other government-approved ID", twoSided: true },
];

export const PROFILE_LINK_FIELDS = [
  ["spotify", "Spotify for Artists", "https://open.spotify.com/artist/..."],
  ["youtube", "YouTube", "https://youtube.com/@yourchannel"],
  ["instagram", "Instagram", "https://instagram.com/yourhandle"],
  ["apple_music", "Apple Music", "https://music.apple.com/artist/..."],
  ["website", "Official website", "https://yourwebsite.com"],
  ["other", "Other music / social platform", "https://..."],
];

export const ROLES = [
  { value: "musician", label: "Musician" },
  { value: "producer", label: "Producer" },
  { value: "songwriter", label: "Songwriter" },
  { value: "band_group", label: "Band / Group" },
];

export const GENRES = ["Pop", "Hip-Hop", "R&B", "Afrobeat", "Electronic", "Indie", "Gospel", "Amapiano"];

export const COUNTRIES = [
  ["Afghanistan", "+93"], ["Albania", "+355"], ["Algeria", "+213"],
  ["American Samoa", "+1"], ["Andorra", "+376"], ["Angola", "+244"],
  ["Anguilla", "+1"], ["Antigua and Barbuda", "+1"], ["Argentina", "+54"],
  ["Armenia", "+374"], ["Aruba", "+297"], ["Australia", "+61"],
  ["Austria", "+43"], ["Azerbaijan", "+994"], ["Bahamas", "+1"],
  ["Bahrain", "+973"], ["Bangladesh", "+880"], ["Barbados", "+1"],
  ["Belarus", "+375"], ["Belgium", "+32"], ["Belize", "+501"],
  ["Benin", "+229"], ["Bermuda", "+1"], ["Bhutan", "+975"],
  ["Bolivia", "+591"], ["Bosnia and Herzegovina", "+387"], ["Botswana", "+267"],
  ["Brazil", "+55"], ["Brunei", "+673"], ["Bulgaria", "+359"],
  ["Burkina Faso", "+226"], ["Burundi", "+257"], ["Cambodia", "+855"],
  ["Cameroon", "+237"], ["Canada", "+1"], ["Cape Verde", "+238"],
  ["Cayman Islands", "+1"], ["Central African Republic", "+236"], ["Chad", "+235"],
  ["Chile", "+56"], ["China", "+86"], ["Colombia", "+57"],
  ["Comoros", "+269"], ["Congo", "+242"], ["Congo (Democratic Republic)", "+243"],
  ["Costa Rica", "+506"], ["Cote d'Ivoire", "+225"], ["Croatia", "+385"],
  ["Cuba", "+53"], ["Curacao", "+599"], ["Cyprus", "+357"],
  ["Czech Republic", "+420"], ["Denmark", "+45"], ["Djibouti", "+253"],
  ["Dominica", "+1"], ["Dominican Republic", "+1"], ["Ecuador", "+593"],
  ["Egypt", "+20"], ["El Salvador", "+503"], ["Equatorial Guinea", "+240"],
  ["Eritrea", "+291"], ["Estonia", "+372"], ["Eswatini", "+268"],
  ["Ethiopia", "+251"], ["Fiji", "+679"], ["Finland", "+358"],
  ["France", "+33"], ["French Guiana", "+594"], ["French Polynesia", "+689"],
  ["Gabon", "+241"], ["Gambia", "+220"], ["Georgia", "+995"],
  ["Germany", "+49"], ["Ghana", "+233"], ["Gibraltar", "+350"],
  ["Greece", "+30"], ["Greenland", "+299"], ["Grenada", "+1"],
  ["Guadeloupe", "+590"], ["Guam", "+1"], ["Guatemala", "+502"],
  ["Guinea", "+224"], ["Guinea-Bissau", "+245"], ["Guyana", "+592"],
  ["Haiti", "+509"], ["Honduras", "+504"], ["Hong Kong", "+852"],
  ["Hungary", "+36"], ["Iceland", "+354"], ["India", "+91"],
  ["Indonesia", "+62"], ["Iran", "+98"], ["Iraq", "+964"],
  ["Ireland", "+353"], ["Israel", "+972"], ["Italy", "+39"],
  ["Jamaica", "+1"], ["Japan", "+81"], ["Jordan", "+962"],
  ["Kazakhstan", "+7"], ["Kenya", "+254"], ["Kiribati", "+686"],
  ["Kosovo", "+383"], ["Kuwait", "+965"], ["Kyrgyzstan", "+996"],
  ["Laos", "+856"], ["Latvia", "+371"], ["Lebanon", "+961"],
  ["Lesotho", "+266"], ["Liberia", "+231"], ["Libya", "+218"],
  ["Liechtenstein", "+423"], ["Lithuania", "+370"], ["Luxembourg", "+352"],
  ["Macau", "+853"], ["Madagascar", "+261"], ["Malawi", "+265"],
  ["Malaysia", "+60"], ["Maldives", "+960"], ["Mali", "+223"],
  ["Malta", "+356"], ["Martinique", "+596"], ["Mauritania", "+222"],
  ["Mauritius", "+230"], ["Mexico", "+52"], ["Moldova", "+373"],
  ["Monaco", "+377"], ["Mongolia", "+976"], ["Montenegro", "+382"],
  ["Montserrat", "+1"], ["Morocco", "+212"], ["Mozambique", "+258"],
  ["Myanmar", "+95"], ["Namibia", "+264"], ["Nepal", "+977"],
  ["Netherlands", "+31"], ["New Caledonia", "+687"], ["New Zealand", "+64"],
  ["Nicaragua", "+505"], ["Niger", "+227"], ["Nigeria", "+234"],
  ["North Korea", "+850"], ["North Macedonia", "+389"], ["Norway", "+47"],
  ["Oman", "+968"], ["Pakistan", "+92"], ["Palestine", "+970"],
  ["Panama", "+507"], ["Papua New Guinea", "+675"], ["Paraguay", "+595"],
  ["Peru", "+51"], ["Philippines", "+63"], ["Poland", "+48"],
  ["Portugal", "+351"], ["Puerto Rico", "+1"], ["Qatar", "+974"],
  ["Reunion", "+262"], ["Romania", "+40"], ["Russia", "+7"],
  ["Rwanda", "+250"], ["Saint Kitts and Nevis", "+1"], ["Saint Lucia", "+1"],
  ["Saint Vincent and the Grenadines", "+1"], ["Samoa", "+685"], ["San Marino", "+378"],
  ["Sao Tome and Principe", "+239"], ["Saudi Arabia", "+966"], ["Senegal", "+221"],
  ["Serbia", "+381"], ["Seychelles", "+248"], ["Sierra Leone", "+232"],
  ["Singapore", "+65"], ["Slovakia", "+421"], ["Slovenia", "+386"],
  ["Solomon Islands", "+677"], ["Somalia", "+252"], ["South Africa", "+27"],
  ["South Korea", "+82"], ["South Sudan", "+211"], ["Spain", "+34"],
  ["Sri Lanka", "+94"], ["Sudan", "+249"], ["Suriname", "+597"],
  ["Sweden", "+46"], ["Switzerland", "+41"], ["Syria", "+963"],
  ["Taiwan", "+886"], ["Tajikistan", "+992"], ["Tanzania", "+255"],
  ["Thailand", "+66"], ["Timor-Leste", "+670"], ["Togo", "+228"],
  ["Tonga", "+676"], ["Trinidad and Tobago", "+1"], ["Tunisia", "+216"],
  ["Turkey", "+90"], ["Turkmenistan", "+993"], ["Turks and Caicos Islands", "+1"],
  ["Uganda", "+256"], ["Ukraine", "+380"], ["United Arab Emirates", "+971"],
  ["United Kingdom", "+44"], ["United States", "+1"], ["Uruguay", "+598"],
  ["Uzbekistan", "+998"], ["Vanuatu", "+678"], ["Vatican City", "+379"],
  ["Venezuela", "+58"], ["Vietnam", "+84"], ["Virgin Islands (British)", "+1"],
  ["Virgin Islands (US)", "+1"], ["Yemen", "+967"], ["Zambia", "+260"],
  ["Zimbabwe", "+263"],
].map(([name, dial]) => ({ name, dial }));

export const DIAL_CODES = Array.from(new Set(COUNTRIES.map((c) => c.dial))).sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)));

export const inputClass = (hasError) =>
  `w-full rounded-xl border px-4 py-2.5 bg-white focus:outline-none focus:ring-2 ${
    hasError ? "border-red-400" : "border-gray-200"
  }`;
export const ringStyle = { "--tw-ring-color": ACCENT };

export function Field({ label, required, hint, error, children }) {
  return (
    <div>
      {label && (
        <label className="block text-sm font-bold text-gray-900 mb-1.5">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      {children}
      {hint && !error && <p className="text-gray-500 text-sm mt-1">{hint}</p>}
      {error && <p className="text-red-600 text-sm mt-1">{error}</p>}
    </div>
  );
}

export function Chip({ selected, onClick, children, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={!!selected}
      className={`rounded-full border px-4 py-1.5 text-sm font-medium transition ${
        selected ? "text-white border-transparent" : "bg-white text-gray-800 border-gray-200 hover:bg-gray-50"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      style={selected ? { backgroundColor: ACCENT } : undefined}
    >
      {children}
    </button>
  );
}

export function PrimaryButton({ children, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={`rounded-full font-semibold text-white px-8 py-3 transition disabled:bg-gray-400 disabled:cursor-not-allowed ${
        props.className || ""
      }`}
      style={{ backgroundColor: props.disabled ? undefined : ACCENT }}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ children, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={`rounded-full font-semibold text-gray-900 border border-gray-200 bg-white px-6 py-3 hover:bg-gray-50 transition disabled:opacity-50 ${
        props.className || ""
      }`}
    >
      {children}
    </button>
  );
}


// ---- input filters --------------------------------------------------------------------------------------------------
// Keep a field to what it may hold while the person types or pastes: digits only for numbers, letters for names.
export const clean = {
  digits: (v) => String(v || "").replace(/\D/g, ""),
  // 12, 12.5, 12.50: digits with at most one dot and two decimals
  decimal: (v) => {
    let s = String(v || "").replace(/[^\d.]/g, "");
    const dot = s.indexOf(".");
    if (dot >= 0) s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, "").slice(0, 2);
    return s.slice(0, 10);
  },
  // People's names: letters (any language), spaces, dots, apostrophes and hyphens
  name: (v) => String(v || "").replace(/[^\p{L}\p{M}\s.'\u2019-]/gu, "").replace(/^\s+/, "").replace(/\s{2,}/g, " "),
  alnum: (v) => String(v || "").replace(/[^A-Za-z0-9-]/g, ""),
  handle: (v) => String(v || "").replace(/[^A-Za-z0-9_.@]/g, ""),
  iswc: (v) => String(v || "").toUpperCase().replace(/[^T0-9.-]/g, ""),
};

// Wraps a react-hook-form register() result so the typed or pasted text is cleaned (and cut to max) before it is stored.
export function filtered(registration, fn, max) {
  return {
    ...registration,
    onChange: (event) => {
      let value = fn(event.target.value);
      if (max) value = value.slice(0, max);
      if (value !== event.target.value) event.target.value = value;
      return registration.onChange(event);
    },
  };
}

// For whole-number <input type="number">: stop e, +, -, . and , which that type otherwise accepts.
export const blockNonInteger = (event) => {
  if (["e", "E", "+", "-", ".", ","].includes(event.key)) event.preventDefault();
};
